import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { programContent, africanCountries } from '../../data/program';
import ProgramNav from '../../components/program/ProgramNav';
import AnimatedSection from '../../components/common/AnimatedSection';
import { 
  ArrowRight, Globe, Check, Users, Building, Shield, 
  Layers, Network, Send, CheckCircle2
} from 'lucide-react';

const BrandedPartiesPage = () => {
  const { language } = useLanguage();
  const content = programContent[language]?.brandedParties || programContent.en.brandedParties;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    country: '',
    email: '',
    background: '',
    motivation: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Application submitted:', formData);
    setFormSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="pt-24">
      <ProgramNav />
      
      {/* Hero */}
      <section className="section bg-gradient-to-b from-[#FFFEF9] to-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5B2D8E]/5 rounded-full mb-6">
                <Network className="w-4 h-4 text-[#5B2D8E]" />
                <span className="text-sm font-medium text-[#5B2D8E]">Federated Structure</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-6">{content.hero.title}</h1>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="text-xl text-[#6B6B6B]" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.hero.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Definition */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="bg-[#5B2D8E]/5 rounded-2xl p-8 lg:p-12 border border-[#5B2D8E]/10">
                <h2 className="heading-2 mb-6 text-[#5B2D8E]">{content.definition.title}</h2>
                <p className="text-lg text-[#2D2D2D] leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
                  {content.definition.content}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Identity System */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Brand Framework</span>
            <h2 className="heading-1 mb-4">{content.identity.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {content.identity.items.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white rounded-xl p-6 border border-[#E8E0D4] h-full">
                  <h3 className="font-semibold text-[#2D2D2D] mb-3">{item.title}</h3>
                  <p className="text-[#6B6B6B]">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Programmatic Cohesion */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">Ideological Foundation</span>
              <h2 className="heading-1 mb-6">{content.cohesion.title}</h2>
              <div className="divider mb-8" />
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <p className="text-lg text-[#2D2D2D] mb-8" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.cohesion.intro}
              </p>
            </AnimatedSection>

            <div className="space-y-4 mb-8">
              {content.cohesion.items.map((item, index) => (
                <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                  <div className="flex items-start gap-4 bg-[#F5F0E8] rounded-xl p-5">
                    <div className="w-8 h-8 bg-[#5B2D8E] rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <p className="text-[#2D2D2D] font-medium">{item}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={0.5}>
              <p className="text-[#6B6B6B] italic border-l-4 border-[#F5A623] pl-4">
                {content.cohesion.note}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Governance Model */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Organizational Structure</span>
            <h2 className="heading-1 mb-4">{content.governance.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-4xl mx-auto">
            {/* Governance Diagram */}
            <AnimatedSection>
              <div className="bg-white rounded-2xl p-8 border border-[#E8E0D4]">
                <div className="space-y-6">
                  {content.governance.bodies.map((body, index) => (
                    <div key={index} className={`flex items-center gap-6 ${
                      index < content.governance.bodies.length - 1 ? 'pb-6 border-b border-[#E8E0D4]' : ''
                    }`}>
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        index === 0 ? 'bg-[#5B2D8E] text-white' :
                        index === 1 ? 'bg-[#F5A623]/20 text-[#D4880D]' :
                        index === 2 ? 'bg-[#0D7377]/10 text-[#0D7377]' :
                        'bg-[#E8E0D4] text-[#6B6B6B]'
                      }`}>
                        {index === 0 ? <Globe className="w-8 h-8" /> :
                         index === 1 ? <Users className="w-8 h-8" /> :
                         index === 2 ? <Shield className="w-8 h-8" /> :
                         <Building className="w-8 h-8" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2D2D2D] mb-1">{body.name}</h3>
                        <p className="text-[#6B6B6B]">{body.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Electoral Strategy */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">Political Action</span>
              <h2 className="heading-1 mb-6">{content.strategy.title}</h2>
              <div className="divider mb-8" />
            </AnimatedSection>

            <div className="space-y-4">
              {content.strategy.items.map((item, index) => (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <div className="flex items-start gap-4">
                    <Check className="w-6 h-6 text-[#5B2D8E] flex-shrink-0 mt-0.5" />
                    <p className="text-lg text-[#2D2D2D]" style={{ fontFamily: 'var(--font-serif)' }}>
                      {item}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Advantages */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Benefits</span>
            <h2 className="heading-1 mb-4">{content.advantages.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {content.advantages.items.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white rounded-xl p-6 border border-[#E8E0D4] h-full">
                  <div className="w-10 h-10 bg-[#5B2D8E]/10 rounded-lg flex items-center justify-center mb-4">
                    <Layers className="w-5 h-5 text-[#5B2D8E]" />
                  </div>
                  <h3 className="font-semibold text-[#2D2D2D] mb-2">{item.title}</h3>
                  <p className="text-[#6B6B6B] text-sm">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="section bg-[#5B2D8E]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-2xl mx-auto">
            <AnimatedSection className="text-center mb-10">
              <h2 className="heading-1 text-white mb-4">{content.cta.title}</h2>
              <p className="text-white/70">{content.cta.disclaimer}</p>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              {!formSubmitted ? (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 space-y-6">
                  <div>
                    <label className="label">{content.cta.form.fullName} *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="label">{content.cta.form.country} *</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="input"
                      >
                        <option value="">Select country</option>
                        {africanCountries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">{content.cta.form.email} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">{content.cta.form.background} *</label>
                    <input
                      type="text"
                      name="background"
                      value={formData.background}
                      onChange={handleChange}
                      required
                      className="input"
                      placeholder="Professional background, political experience, etc."
                    />
                  </div>

                  <div>
                    <label className="label">{content.cta.form.motivation} *</label>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleChange}
                      required
                      className="textarea"
                      rows={5}
                      maxLength={1500}
                      placeholder="Why do you want to launch an ADA section in your country?"
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full">
                    {content.cta.form.submit}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <div className="bg-white rounded-2xl p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-6 text-[#2E7D32]" />
                  <h3 className="heading-3 mb-4">Application Submitted</h3>
                  <p className="text-[#6B6B6B] mb-6">
                    Thank you for your interest in launching an ADA section. We will review your application and contact you.
                  </p>
                  <Link to="/program" className="btn-secondary">
                    Return to Program
                  </Link>
                </div>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandedPartiesPage;
